import React, { useCallback, useMemo } from 'react'
import _ from 'lodash'

import {
  Input,
  TranslatedInput,
  Checkbox,
  Slider,
} from  '../../../components'

import { required, maxValue } from '../../../common/validators'

import S from './styled'

const maxValueValidator = maxValue(360)

const timeValidator = [required, maxValueValidator]

const OverrideLabelInput = ({
  field, title, defaultLocale, text, model, locales, handleTranslationChange,
  defaultValue,
  disabled,
}) => (
  <TranslatedInput {...{
    name: `${field}.${defaultLocale}`,
    text,
    title: title,
    defaultValue,
    validate: required,
    minWidth: '160px',
    locales,
    defaultLocale,
    translatedField: `${field}`,
    translates: _.get(model, `${field}`),
    onTranslationChange: handleTranslationChange,
    disabled,
  }}/>
)

const Variation = ({
  locales, ingredients, defaultLocale, model: variationModel, field,
  browning, cooking,
  locked,
}) => {
  const prefix = useMemo(() => path => `values[${field}].${path}`, [field])

  const { number, text, model, checkbox, onChange } = variationModel

  const handleTranslationChange = useCallback((field, translates) => {
    onChange({
      target: {
        value: translates,
        name: field,
      },
    })
  }, [onChange])

  return (
    <S.Variation>
      <S.Times>
        <Input {...{
          name: prefix`preparationTimeMinutes`, number,
          title: 'Prep Time (min)',
          validate: timeValidator,
          min: 0,
          disabled: locked,
        }}
        />
        <Input {...{
          name: prefix`cookTimeMinutes`, number,
          title: 'Cook Time (min)',
          validate: timeValidator,
          min: 0,
          disabled: locked,
        }}
        />
        <Input {...{
          name: prefix`restTimeMinutes`, number,
          title: 'Rest Time (min)',
          validate: timeValidator,
          min: 0,
          disabled: locked,
        }}
        />
      </S.Times>
      <S.LevelsBlock>
        <S.Level>
          <div>
            <Checkbox {...checkbox({
              name: prefix`hasBrowningLevel`,
              title: 'Has Browning Level',
              readOnly: locked,
            })}/>
          </div>
          {
            _.get(model, prefix`hasBrowningLevel`) ? (
              <div>
                <S.LevelValues>
                  <Slider
                    {...number({ name: prefix`browning.minLevel` })}
                    max={_.get(model, prefix`browning.maxLevel`)}
                    readOnly={locked}
                  />
                  <Slider
                    {...number({ name: prefix`browning.maxLevel` })}
                    min={_.get(model, prefix`browning.minLevel`)}
                    readOnly={locked}
                  />
                </S.LevelValues>
              </div>
            ) : null
          }
        </S.Level>
        <S.Level>
          <div>
            <Checkbox {...checkbox({
              name: prefix`browning.translates.defaultLabels`,
              title: 'Overwrite Labels',
              invertCheck: true,
            })} readOnly={locked}/>
          </div>
          {
            !_.get(model, prefix`browning.translates.defaultLabels`) ? (
              <div>
                <S.LevelValues>
                  <OverrideLabelInput
                    {...{ field: prefix`browning.translates.level0`, title: 'Light',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: browning.level0[defaultLocale],
                      disabled: locked,
                    }}
                  />
                  <OverrideLabelInput
                    {...{ field: prefix`browning.translates.level1`, title: 'Lighter',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: browning.level1[defaultLocale],
                      disabled: locked,
                    }}
                  />
                  <OverrideLabelInput
                    {...{ field: prefix`browning.translates.level2`, title: 'Medium',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: browning.level2[defaultLocale],
                      disabled: locked,
                    }}
                  />
                  <OverrideLabelInput
                    {...{ field: prefix`browning.translates.level3`, title: 'Darker',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: browning.level3[defaultLocale],
                      disabled: locked,
                    }}
                  />
                  <OverrideLabelInput
                    {...{ field: prefix`browning.translates.level4`, title: 'Dark',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: browning.level4[defaultLocale],
                      disabled: locked,
                    }}
                  />
                </S.LevelValues>
              </div>
            ) : null
          }
        </S.Level>
        <S.Level>
          <div>
            <Checkbox {...checkbox({
              name: prefix`hasCookingLevel`,
              title: 'Has Cooking Level',
            })} readOnly={locked}/>
          </div>
          {
            _.get(model, prefix`hasCookingLevel`) ? (
              <div>
                <S.LevelValues>
                  <Slider
                    {...number({ name: prefix`cooking.minLevel` })}
                    max={_.get(model, prefix`cooking.maxLevel`)}
                    readOnly={locked}
                  />
                  <Slider
                    {...number({ name: prefix`cooking.maxLevel` })}
                    min={_.get(model, prefix`cooking.minLevel`)}
                    readOnly={locked}
                  />
                </S.LevelValues>
              </div>
            ) : null
          }
        </S.Level>
        <S.Level>
          <div>
            <Checkbox {...checkbox({
              name: prefix`cooking.translates.defaultLabels`,
              title: 'Overwrite Labels',
              invertCheck: true,
            })} readOnly={locked} />
          </div>
          {
            !_.get(model, prefix`cooking.translates.defaultLabels`) ? (
              <div>
                <S.LevelValues>
                  <OverrideLabelInput
                    {...{ field: prefix`cooking.translates.level0`, title: 'Light',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: cooking.level0[defaultLocale],
                      disabled: locked,
                    }}
                  />
                  <OverrideLabelInput
                    {...{ field: prefix`cooking.translates.level1`, title: 'Lighter',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: cooking.level1[defaultLocale],
                      disabled: locked,
                    }}
                  />
                  <OverrideLabelInput
                    {...{ field: prefix`cooking.translates.level2`, title: 'Medium',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: cooking.level2[defaultLocale],
                      disabled: locked,
                    }}
                  />
                  <OverrideLabelInput
                    {...{ field: prefix`cooking.translates.level3`, title: 'Darker',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: cooking.level3[defaultLocale],
                      disabled: locked,
                    }}
                  />
                  <OverrideLabelInput
                    {...{ field: prefix`cooking.translates.level4`, title: 'Dark',
                      defaultLocale, locales, text, model, handleTranslationChange,
                      defaultValue: cooking.level4[defaultLocale],
                      disabled: locked,
                    }}
                  />
                </S.LevelValues>
              </div>
            ) : null
          }
        </S.Level>
        <S.Level>
          <div>
            <Checkbox {...checkbox({ name: prefix`hasTime`, title: 'Has Cooking Time' })} readOnly={locked}/>
          </div>
          <div />
        </S.Level>
      </S.LevelsBlock>
    </S.Variation>
  )
}

export default Variation
